import { DOCUMENT } from '@angular/common';
import { AfterViewInit, Component, EventEmitter, Inject, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild, ViewContainerRef } from '@angular/core';
import { GlobalFunctions } from '@global/GlobalFunctions';
import { CircularGaugeComponent } from '@syncfusion/ej2-angular-circulargauge';
import { Subject, combineLatest, takeUntil, distinctUntilChanged, filter, interval, timestamp } from 'rxjs';
import { PanelData } from '../custom-dashboard/custom-dashboard.component';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { AxisModel, ChartComponent, FontModel } from '@syncfusion/ej2-angular-charts';
import { MatMenu } from '@angular/material/menu';
import { AssetDataPoint, ISensorData } from '@services/datapoints-data.service';

@Component({
	selector: 'app-dashboard-chart',
	templateUrl: './dashboard-chart.component.html',
	styleUrls: ['./dashboard-chart.component.scss']
})
export class DashboardChartComponent implements AfterViewInit, OnInit, OnChanges, OnDestroy {

	/** Observable Subject Finalise */
	private finalise = new Subject<void>();

	/** panelData */
	@Input() panelData: any;

	/** publicPanelData */
	@Input() publicPanelData: PanelData[];

	/** refresh */
	@Input() refresh: boolean;

	/** creating */
	@Input() creating: boolean;

	/** numDays */
	@Input() numDays: number;

	/** titleMenu */
	@Input() titleMenu: MatMenu;

	/** chart */
	@ViewChild('chart') chart!: ChartComponent;

	/** Chart Legend */
	public legend: Object;

	/** Chart Zoom Settings */
	public zoomSettings: Object;

	/** crosshair */
	public crosshair: object = {
		enable: true,
		lineType: 'Vertical'
	};

	/** Sensor pen Pallet Colours */
	public palette: object;

	/** Tooltip Settings */
	public tooltipSettings: Object = {
		enable: true,
		enableAnimation: false,
		fadeOutDuration: 0,
		shared: true // Enables shared tooltip
	};

	/** Chart boarder Settings */
	public borderSettings: Object;

	/**
	 * Chart Selection mode
	 * None|Point|Series|Cluster|DragXY|DragX|DragY
	 */
	public selectionMode: string;

	/** Sensor X Axis */
	public sensorXAxis: Object;

	/** titleStyle */
	public titleStyle: FontModel = {
		fontWeight: "bold",
		size: "large"
	}

	/** intervalFinish */
	@Output() intervalFinish = new EventEmitter<any>();

	/** Page Refresh Interval object */
	private refreshInterval: any;

	/** refreshTime */
	private refreshTime: number = 0.5;

	/** isHierarchyNode */
	public isHierarchyNode: boolean = false;

	/** seriesChange */
	@Output() seriesChange = new EventEmitter<any>();

	/** placeHolderData */
	public placeHolderData: ISensorData[] = [
		{
			timestamp: 0,
			sampleDate: new Date(0).toString(),
			sample_data: 1
		},
		{
			timestamp: 1000,
			sampleDate: new Date(1000).toString(),
			sample_data: 2
		},
		{
			timestamp: 2000,
			sampleDate: new Date(2000).toString(),
			sample_data: 3
		},
		{
			timestamp: 3000,
			sampleDate: new Date(3000).toString(),
			sample_data: 2
		},
		{
			timestamp: 4000,
			sampleDate: new Date(4000).toString(),
			sample_data: 1
		},
		{
			timestamp: 5000,
			sampleDate: new Date(5000).toString(),
			sample_data: 6
		},
		{
			timestamp: 6000,
			sampleDate: new Date(6000).toString(),
			sample_data: 7
		},
		{
			timestamp: 7000,
			sampleDate: new Date(7000).toString(),
			sample_data: 10
		},
	];

	/** showBars */
	public showBars: boolean = false;

	/** narrowPanel */
	public narrowPanel: boolean = false;

	/** days */
	public days: string[] = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

	/** axes */
	public axes: AxisModel[] = [];

	/** axesReady */
	public axesReady: boolean = false;

	/**
	 * Default Constructor
	 * @param globalFunctions globalFunctions  
	 * @param siteSubscriptions siteSubscriptions 
	 * @param document document 
	 * @param vcr vcr 
	 */
	constructor(
		public globalFunctions: GlobalFunctions,
		private siteSubscriptions: siteSubscriptions,
		@Inject(DOCUMENT) public document: Document,
		private vcr: ViewContainerRef
	) {
		/** Get required subscriptions */
		combineLatest([
			this.siteSubscriptions.selectedOrganisation$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
		]).pipe(
			// Ensure that subscriptions are not null or undefined
			filter(([selectedAccount]) => !!selectedAccount)
		).subscribe({
			next: ([selectedAccount]) => {
			},
			error: (error) => {
				this.globalFunctions.HandleError(error, "Error: Error in one of the combined subscriptions", false);
			}
		});
	}

	/**
	 * ngOnInit
	 */
	ngOnInit(): void {
		if (this.panelData) {
			this.refreshInterval = interval((this.refreshTime * 60000)).pipe(takeUntil(this.finalise)).subscribe(() => {
				this.intervalFinish.emit(this.panelData);
			});
			this.isHierarchyNode = this.panelData.isHierarchy ?? false;
			this.showBars = this.panelData.barDefault ?? false;
		}

		this.legend = {
			visible: false
		}
	}

	/**
	 * ngAfterViewInit
	 */
	ngAfterViewInit(): void {
		this.setXAxis();
		this.setYAxis();
		this.tooltipSettings = {
			enable: true,
			enableAnimation: false,
			fadeOutDuration: 0,
			shared: true // Enables shared tooltip
		};
		if (this.creating) {
			this.plotSampleData(this.showBars);
		}
		if (this.panelData) {
			this.checkIfNarrowPanel();
		}
	}

	/**
	 * ngOnChanges
	 * @param changes changes  
	 */
	ngOnChanges(changes: SimpleChanges): void {
		if (changes["refresh"] && changes["refresh"].currentValue == true) {
			this.respondToRefresh();
		}
		if (changes["publicPanelData"]) {
			this.setYAxis();
		}
	}

	/**
	 * ngOnDestroy
	 */
	ngOnDestroy(): void {
		this.finalise.next();
		this.finalise.complete();
		if (this.refreshInterval) {
			clearInterval(this.refreshInterval);  // Clear the interval if it's set
			this.refreshInterval = null;  // Reset to null to prevent memory leaks
		}
	}

	/**
	 * respondToRefresh
	 */
	respondToRefresh() {
		if (this.chart) {
			this.setYAxis();
			let parent: HTMLElement | null = this.chart.element;
			let panel: HTMLElement | undefined = undefined;
			while (parent) {
				if (parent.classList.contains("panel-host")) {
					panel = parent;
					parent = null;
					break;
				} else {
					parent = parent.parentElement;
				}
			}
			if (panel) {
				const height: number = panel.offsetHeight ?? 0;
				const width: number = panel.offsetWidth ?? 0;

				const title: HTMLElement | null = <HTMLElement>panel.getElementsByClassName("text-panel-title")[0];

				this.chart.height = `${(!title ? (height * 0.825) : (height - title.offsetHeight))}px`;
				this.chart.width = `${width}px`;
				this.chart.refresh();
			}
		}
	}

	/**
	 * toggleShowDeltaBars
	 */
	toggleShowDeltaBars(panelData: any) {
		const data = this.publicPanelData[panelData.id];
		if (data.activeSeries) {
			if (data.activeSeries.includes(data.series![0])) {
				this.chart.primaryXAxis.valueType = 'Category';
				this.chart.primaryXAxis.labelFormat = '';
				data.activeSeries = [data.barSeries!];
				this.seriesChange.emit({ panelID: this.panelData.id, barSeries: true });
			} else {
				this.chart.primaryXAxis.valueType = 'DateTime';
				this.chart.primaryXAxis.labelFormat = this.numDays > 2 ? "dd-MMM" : "HH:mm dd-MMM",
					data.activeSeries = data.series;
				this.seriesChange.emit({ panelID: this.panelData.id, barSeries: false });
			}
		}
	}

	/**
	 * Add a section to the graph
	 * @param lines stripLine Definition
	 */
	setXAxis(lines: object[] = []): void {

		/* Sensor Graph specific variables */
		this.sensorXAxis = {
			// title: 'Date',
			valueType: !this.showBars ? "DateTime" : 'Category',
			intervalType: this.numDays > 2 ? "Days" : "Hours",
			// intervalType: "Days",
			// intervalType: "Hours",
			// interval: 1,
			labelFormat: this.numDays > 2 ? "dd-MMM" : "HH:mm dd-MMM-yy",
			// labelFormat: "HH:mm:ss dd-MMM",
			// labelIntersectAction: "Rotate45",
			labelRotation: -35,
			// labelRotation: 90,
			majorTickLines: { visible: false },
			minorTickLines: { visible: false },
			majorGridLines: { visible: false },
			minorGridLines: { visible: false },
			// stripLines: lines

		};
	}

	/**
	 * setYAxis
	 */
	setYAxis() {
		if (this.chart) {
			if (this.isHierarchyNode) {
				this.chart.axes = [];
				let uniqueSIs: string[] = [];
				this.panelData.datapoints.forEach((datapoint: AssetDataPoint) => {
					if (!uniqueSIs.includes(datapoint.SI)) {
						uniqueSIs.push(datapoint.SI);
					}
				});
				let axes: AxisModel[] = [];
				uniqueSIs.forEach((si: string, index: number) => {
					let axis: AxisModel = {
						name: `Axis${si}`,
						opposedPosition: index % 2 != 0,
						labelFormat: `{value}${si}`
					}
					axes.push(axis);
				});
				this.axes = axes;
				// this.chart.primaryYAxis = axes[0];
				this.axesReady = true;
			} else {
				if (this.panelData && this.panelData.datapoints && this.panelData.datapoints.length > 0) {
					this.chart.primaryYAxis.labelFormat = `{value}${this.panelData.datapoints[0].SI}`;
				}
				// this.chart.axes = [];
				// let uniqueSIs: string[] = [];
				// this.panelData.datapoints.forEach((datapoint: AssetDataPoint) => {
				// 	if (!uniqueSIs.includes(datapoint.SI)) {
				// 		uniqueSIs.push(datapoint.SI);
				// 	}
				// });
				// let axes: AxisModel[] = [];
				// uniqueSIs.forEach((si: string, index: number) => {
				// 	let axis: AxisModel = {
				// 		name: `Axis${si}`,
				// 		opposedPosition: index % 2 != 0,
				// 		labelFormat: `{value}${si}`
				// 	}
				// 	axes.push(axis);
				// });
				// this.axes = axes;
				// // this.chart.primaryYAxis = axes[0];
				// this.axesReady = true;
			}
		} else {
			setTimeout(() => {
				this.setYAxis();
			}, 100);
		}
	}

	/** 
	 * plotSampleData 
	 */
	plotSampleData(barDefault: boolean = false) {
		this.chart.series = [];
		this.chart.addSeries([{
			animation: {
				enable: false
			},
			emptyPointSettings: { mode: "Drop" },
			type: barDefault ? 'Column' : 'Line',
			dataSource: this.placeHolderData,
			name: "Sample Data",
			xName: 'timestamp',
			yName: `sample_data`,
			marker: {
				visible: false,
				width: 8,
				height: 8,
				shape: 'Triangle',
				dataLabel: { "name": 'x' },
				isFilled: true
			},
			// yAxisName: `Axis${this.targetSI}`,
			fill: "lightseagreen",
			visible: true
		}]);
	}

	/**
	 * toggleSampleDataBars
	 */
	toggleSampleDataBars() {
		this.showBars = !this.showBars;
		if (this.showBars) {
			this.chart.series[0].type = "Column";
		} else {
			this.chart.series[0].type = "Line";
		}
		this.seriesChange.emit({ panelID: this.panelData.id, barSeries: this.showBars });
		this.chart.refresh();
	}

	/**
	 * checkIfNarrowPanel
	 */
	checkIfNarrowPanel() {
		const hosts = Array.from(this.document.getElementsByClassName("panel-host"));
		const host = hosts.find((x) => x.id == `${this.panelData.id}`);
		if (host) {
			this.narrowPanel = host.clientWidth < 500;
		}
	}

	/**
	 * beforeSeriesRender
	 * @param event event 
	 */
	beforeSeriesRender(event: any) {
		if (event.series.type == 'Column') {
			event.data = event.data.map((x: ISensorData) => ({
				...x,
				timestamp: this.formatNarrowBarLabel(new Date(x.sampleDate))
			}));
		}
	}

	/**
	 * formatNarrowBarLabel
	 * @param date date 
	 * @returns 
	 */
	formatNarrowBarLabel(date: Date): string {
		if (this.numDays <= 2) {
			return `${date.getHours()}:00 ${this.days[date.getDay()]}`;
		} else {
			const suffix: string = [1, 31].includes(date.getDate()) ? "st" : date.getDate() == 2 ? "nd" : date.getDate() == 3 ? "rd" : "th"
			return `${this.days[date.getDay()]} ${date.getDate()}${suffix}`;
		}
	}
}
